import { useEffect } from 'react';

import { useDispatch } from 'react-redux';
import {
  ANNOUNCEMENT,
  fetchRetry,
  FRONT_PATIENT_MAP_SERVER,
} from '@docavenue/core';
import config from '@/src/config';

type ServerConfig = {
  enabled: boolean;
  url: string;
};

const templateRe = /\{ *([\w_ -]+) *\}/g;

// Simple templating facility, accepts a template string of the form `'Hello {a}, {b}'`
// and a data object like `{a: 'foo', b: 'bar'}`, returns evaluated string
// `('Hello foo, bar')`.
const template = (s: string, data: Record<string, string>) => {
  return s.replace(templateRe, (str, key) => {
    const value = data[key];
    if (value === undefined) {
      throw new Error(`No value provided for variable ${str}`);
    }
    return value;
  });
};

async function checkImageUrl(url: string) {
  return new Promise(resolve => {
    const img = new Image();

    img.src = url;
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
  });
}

async function getBestServer(
  servers: ServerConfig[],
  fallbackServerUrl: string,
) {
  const checkPromises = servers.map(server =>
    server.enabled
      ? checkImageUrl(template(server.url, { s: 'a', z: '10', x: '0', y: '0' }))
      : Promise.resolve(false),
  );

  const results = await Promise.all(checkPromises);

  for (let i = 0; i < results.length; i++) {
    if (results[i]) {
      return servers[i].url;
    }
  }

  return fallbackServerUrl;
}

/**
 * Get the settings of the map server.
 * @returns null
 */
const MapServerSettingsDetector = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    fetchRetry(
      fetch,
      `${config.get('API_ENDPOINT')}/pat-public/mapSettings`,
      1000,
      30,
      {},
      true,
    )
      .then((res: Response) => res.json())
      .catch(() => [])
      .then(async (res: { mapServerSettingsList: ServerConfig[] }) => {
        const mapServerUrl = await getBestServer(
          res?.mapServerSettingsList ?? [],
          'https://{s}.tile.osm.org/{z}/{x}/{y}.png',
        );
        dispatch({
          type: ANNOUNCEMENT,
          payload: {
            service: FRONT_PATIENT_MAP_SERVER,
            channel: 'mapServerUrl',
            url: mapServerUrl,
          },
        });
      });
  }, []);

  return null;
};
export default MapServerSettingsDetector;
